import { useState } from 'react';
import type { AdoptionByModelPoint, ModelDeveloperBreakdownPoint, PremiumQuotaByModelPoint } from '@ace-tracker/shared';
import { AIU_DEFINITION } from '../metricInfo';
import { useDeveloperProfiles } from '../settings';
import { ExportMenu } from './ExportMenu';
import { InfoTooltip, Term } from './InfoTooltip';

/** Attribution labels are never merged — one record belongs to exactly one editor. */
const EDITOR_ORDER = ['VS Code', 'Visual Studio', 'Unknown'] as const;

const EDITOR_CHIP: Record<string, string> = {
  'VS Code': 'bg-primary-blue/10 text-primary-blue',
  'Visual Studio': 'bg-info/10 text-info',
  Unknown: 'bg-surface-alt text-text-muted',
};

function editorChipClass(editor: string): string {
  return EDITOR_CHIP[editor] ?? EDITOR_CHIP.Unknown;
}

/** Distinct editors that produced usage for a model, in a stable order, never concatenated. */
function editorsForModel(rows: ModelDeveloperBreakdownPoint[]): string[] {
  const seen = new Set(rows.map((r) => r.editor));
  const ordered = EDITOR_ORDER.filter((editor) => seen.has(editor));
  const extras = [...seen].filter((editor) => !(EDITOR_ORDER as readonly string[]).includes(editor));
  return [...ordered, ...extras];
}

/** Groups a model's rows by developer so each developer's per-editor split renders together. */
function groupByDeveloper(
  rows: ModelDeveloperBreakdownPoint[],
): { developerId: string; editors: ModelDeveloperBreakdownPoint[]; calls: number }[] {
  const map = new Map<string, ModelDeveloperBreakdownPoint[]>();
  for (const r of rows) {
    const list = map.get(r.developerId) ?? [];
    list.push(r);
    map.set(r.developerId, list);
  }
  return [...map.entries()]
    .map(([developerId, editors]) => ({
      developerId,
      editors: [...editors].sort(
        (a, b) => EDITOR_ORDER.indexOf(a.editor as (typeof EDITOR_ORDER)[number]) - EDITOR_ORDER.indexOf(b.editor as (typeof EDITOR_ORDER)[number]),
      ),
      calls: editors.reduce((sum, e) => sum + e.events, 0),
    }))
    .sort((a, b) => b.calls - a.calls);
}

export function DrillDownTable({
  byModel,
  premiumQuotaByModel,
  breakdown,
  currency,
  indicativeCostPerAiu,
  info,
}: {
  byModel: AdoptionByModelPoint[];
  premiumQuotaByModel: PremiumQuotaByModelPoint[];
  breakdown: ModelDeveloperBreakdownPoint[];
  currency: string;
  indicativeCostPerAiu: number;
  info?: string;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const { label } = useDeveloperProfiles();
  const aiuByModel = new Map(premiumQuotaByModel.map((m) => [m.model, m]));

  return (
    <div data-export-root className="rounded-card border border-border bg-surface p-5 shadow-sm">
      <ExportMenu title="Drill-down explorer" className="float-right -mr-2 -mt-2 ml-2" />
      <h3 className="flex items-center gap-1.5 text-sm font-medium text-text-default">
        Drill-down explorer
        {info && <InfoTooltip text={info} />}
      </h3>
      <p className="mt-1 text-xs text-text-muted">Model → developer. Click a row to expand.</p>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
            <tr>
              <th className="pb-2.5 pr-3">Model</th>
              <th className="pb-2.5 pr-3 text-right">Calls</th>
              <th className="pb-2.5 pr-3 text-right">
                <Term title={AIU_DEFINITION}>AIU</Term>
              </th>
              <th className="pb-2.5 pr-3 text-right">
                <Term title={AIU_DEFINITION}>AIU</Term> / call
              </th>
              <th className="pb-2.5 pr-3 text-right">AIU estimate</th>
              <th className="pb-2.5 pr-3 text-right">Input tokens</th>
              <th className="pb-2.5 pr-3 text-right">Output tokens</th>
              <th className="pb-2.5">Editor</th>
            </tr>
          </thead>
          <tbody>
            {byModel.map((m) => {
              const isOpen = expanded === m.model;
              const quota = aiuByModel.get(m.model);
              const rows = breakdown.filter((b) => b.model === m.model);
              return (
                <>
                  <tr
                    key={m.model}
                    tabIndex={0}
                    role="button"
                    aria-expanded={isOpen}
                    onClick={() => setExpanded(isOpen ? null : m.model)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setExpanded(isOpen ? null : m.model);
                      }
                    }}
                    className="cursor-pointer border-b border-border hover:bg-surface-alt focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-sky-blue"
                  >
                    <td className="py-2.5 pr-3 font-medium text-text-default">
                      <span className="inline-flex items-center gap-2">
                        <span
                          className={`inline-block h-0 w-0 border-y-4 border-y-transparent border-l-[5px] border-l-text-faint transition-transform ${isOpen ? 'rotate-90' : ''}`}
                          aria-hidden="true"
                        />
                        {m.model}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{m.events}</td>
                    <td className="py-2.5 pr-3 text-right font-semibold tabular-nums text-text-default">
                      {(quota?.aiu ?? 0).toFixed(2)}
                    </td>
                    <td className="py-2.5 pr-3 text-right tabular-nums text-text-muted">{(quota?.aiuPerCall ?? 0).toFixed(2)}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums text-text-muted">
                      {formatCurrency(quota?.indicativeCost ?? 0, currency)}
                    </td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{m.inputTokens.toLocaleString()}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{m.outputTokens.toLocaleString()}</td>
                    <td className="py-2.5">
                      <span className="inline-flex flex-wrap gap-1">
                        {editorsForModel(rows).length > 0 ? (
                          editorsForModel(rows).map((editor) => (
                            <span
                              key={editor}
                              className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${editorChipClass(editor)}`}
                            >
                              {editor}
                            </span>
                          ))
                        ) : (
                          <span className="text-text-faint">—</span>
                        )}
                      </span>
                    </td>
                  </tr>
                  {isOpen &&
                    (rows.length > 0 ? (
                      groupByDeveloper(rows).map((dev) => (
                        <tr key={`${m.model}-${dev.developerId}`} className="border-b border-border bg-surface-alt">
                          <td colSpan={8} className="py-2.5 pl-7 pr-3 text-xs text-text-muted">
                            <div className="font-medium text-text-default">
                              {label(dev.developerId)} · {dev.calls} calls total
                            </div>
                            <ul className="mt-1 space-y-1">
                              {dev.editors.map((e) => (
                                <li key={e.editor} className="flex flex-wrap items-center gap-2">
                                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${editorChipClass(e.editor)}`}>
                                    {e.editor}
                                  </span>
                                  <span className="tabular-nums">
                                    {e.events} calls · {e.inputTokens.toLocaleString()} input · {e.outputTokens.toLocaleString()} output ·{' '}
                                    {formatCurrency(e.aiu * indicativeCostPerAiu, currency)} AIU estimate
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr className="border-b border-border bg-surface-alt">
                        <td colSpan={8} className="py-2.5 pl-7 pr-3 text-xs text-text-muted">
                          No per-developer data in this range.
                        </td>
                      </tr>
                    ))}
                </>
              );
            })}
            {byModel.length === 0 && (
              <tr>
                <td colSpan={8} className="py-3 text-text-muted">
                  No usage in this range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-4 border-t border-border pt-3 text-xs text-text-faint">
        The AIU estimate is recorded AIU at the configured planning rate — a legacy source metric, not GitHub AI-credit billing
        or current spend. It excludes seat licences, allowances,
        discounts, taxes, and contract-specific pricing. Developer identities are salted hashes or usernames, depending on collector
        config. The Editor column attributes each request to the IDE that produced it — VS Code and Visual Studio are tracked
        separately and never merged; requests with no editor signal show as Unknown.
      </p>
    </div>
  );
}

function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}
